/** @file jointRotation: the yaw/pitch/roll the inspector shows for a node at the playhead, and from where. */
import type { Clip, NodeKey } from '@zilo/pose3d-format/types/clip';
import type { PoseOverride } from '../../state/editorTypes';
import { nodeKeyOf } from './nodeKeyOf';

/** Shown rotation; `source` says whether it is being dragged, keyed here, inherited from an earlier key, or the rest. */
export type JointRotation = {
  rot: [number, number, number];
  source: 'override' | 'key' | 'earlier' | 'rest';
  /** The frame of the inherited key (`earlier` only). */
  from: number | null;
  /** The node's key at this frame (kept under a new rotation), or null. */
  exact: NodeKey | null;
};

/** The dragged rotation of `node` when the override is for this clip and frame. */
function draggedRot(clipName: string, frame: number, node: string, override: PoseOverride | null): [number, number, number] | undefined {
  if (override === null || override.clip !== clipName || override.frame !== frame) return undefined;
  return override.nodes[node]?.rot;
}

/** Override (same clip and frame) first, then the key at `frame`, then the latest earlier rot key, then `rest` or zero. */
export function jointRotation(
  clipName: string, clip: Pick<Clip, 'keys'>, frame: number, node: string,
  override: PoseOverride | null, rest: [number, number, number] | undefined,
): JointRotation {
  const here = nodeKeyOf(clip, frame, node);
  const exact = here !== null && here.exact ? here.key : null;
  const dragged = draggedRot(clipName, frame, node, override);
  if (dragged !== undefined) return { rot: dragged, source: 'override', from: null, exact };
  const view = nodeKeyOf(clip, frame, node, 'rot');
  const rot = view?.key.rot;
  if (view === null || rot === undefined) return { rot: rest ?? [0, 0, 0], source: 'rest', from: null, exact };
  return view.exact ? { rot, source: 'key', from: null, exact } : { rot, source: 'earlier', from: view.frame, exact };
}
